import { GraphQLCameraAssociationRequester } from '@cobliteam/api-lib';

import type { Association, AssociationConfig } from '@cobliteam/common-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const QUERY_KEY = 'CAMERA-ASSOCIATION';

const useCameraAssociation = () => {
  const queryClient = useQueryClient();

  const useCameraAssociationQuery = () => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    const cameraAssociationGraphQLRequester = new GraphQLCameraAssociationRequester();

    return useQuery({
      queryKey: [QUERY_KEY],
      queryFn: () => cameraAssociationGraphQLRequester.getCameraAssociation(),
      retry: false,
      enabled: true,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      staleTime: 300000,
    });
  };

  const updateCameraAssociation = useMutation<boolean, unknown, { updateCameraAssociation: AssociationConfig }, { previousValue?: Association }>({
    mutationFn: (request) => {
      const cameraAssociationGraphQLRequester = new GraphQLCameraAssociationRequester();
      return cameraAssociationGraphQLRequester.updateCameraAssociation(request.updateCameraAssociation);
    },
    // The optimistic update has to preserve the `Association` shape: `face-id.tsx` reads
    // `associationConfig.entireFleetAssociationConfig.isOn` back out of this cache entry to
    // decide whether the form is dirty. Spreading the raw `getQueriesData` tuples in here
    // dropped `associationConfig` mid-save, which flipped the switch off until the refetch.
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: [QUERY_KEY] });
      const previousValue = queryClient.getQueryData<Association>([QUERY_KEY]);
      queryClient.setQueryData([QUERY_KEY], (currentValue: Association | undefined) =>
        currentValue
          ? {
              ...currentValue,
              associationConfig: {
                ...currentValue.associationConfig,
                ...variables.updateCameraAssociation,
              },
            }
          : currentValue
      );
      return { previousValue };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousValue) {
        queryClient.setQueryData([QUERY_KEY], context.previousValue);
      }
    },
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });

  return {
    useCameraAssociationQuery,
    updateCameraAssociation,
    invalidate,
  };
};

export default useCameraAssociation;
